/**
 * Cover Art (Image LSB) steganography — client-side Canvas API implementation.
 *
 * Encoding format (compatible with Python Pillow implementation):
 *   Pixel traversal: row-major (top→bottom, left→right), RGB channels (skip alpha)
 *   Bit order: 1 LSB per color channel, MSB-first within each payload byte
 *   Layout:
 *     [0..31]   32 LSBs → uint32 BE: total blob length
 *     [32..N]   blob_length × 8 LSBs → blob bytes
 *   Output: PNG (lossless)
 */

/**
 * Embed a blob into an image's pixel LSBs.
 * Returns the encoded image as a PNG Uint8Array.
 */
export async function embedInCoverArt(
  imageData: Uint8Array,
  blob: Uint8Array
): Promise<Uint8Array> {
  const bitmap = await createImageBitmap(new Blob([imageData as BlobPart]));
  const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(bitmap, 0, 0);

  const imgData = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
  const pixels = imgData.data; // RGBA flat array

  // Count available RGB channels (skip every 4th byte = alpha)
  const availableBits = bitmap.width * bitmap.height * 3;
  const requiredBits = 32 + blob.length * 8;

  if (requiredBits > availableBits) {
    throw new Error(
      `Payload too large: needs ${requiredBits} bits, image has ${availableBits} RGB channels`
    );
  }

  // Build length prefix (4 bytes, big-endian) + blob
  const lengthPrefix = new Uint8Array(4);
  new DataView(lengthPrefix.buffer).setUint32(0, blob.length, false);

  const data = new Uint8Array(4 + blob.length);
  data.set(lengthPrefix, 0);
  data.set(blob, 4);

  // Embed into RGB channels (skip alpha at every 4th index)
  let bitIdx = 0;
  for (const byte of data) {
    for (let bitPos = 7; bitPos >= 0; bitPos--) {
      const bit = (byte >> bitPos) & 1;
      const pixelIdx = rgbToRgbaIndex(bitIdx);
      pixels[pixelIdx] = (pixels[pixelIdx]! & 0xfe) | bit;
      bitIdx++;
    }
  }

  ctx.putImageData(imgData, 0, 0);
  const pngBlob = await canvas.convertToBlob({ type: "image/png" });
  return new Uint8Array(await pngBlob.arrayBuffer());
}

/**
 * Extract a blob from an image's pixel LSBs.
 * Returns the blob bytes, or null if the length field is invalid.
 */
export async function extractFromCoverArt(
  imageData: Uint8Array
): Promise<Uint8Array | null> {
  const bitmap = await createImageBitmap(new Blob([imageData as BlobPart]));
  const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(bitmap, 0, 0);

  const imgData = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
  const pixels = imgData.data;

  const availableBits = bitmap.width * bitmap.height * 3;

  // Need at least 32 bits for length prefix
  if (availableBits < 32) return null;

  // Extract length prefix (32 LSBs → 4 bytes → uint32 BE)
  const lengthBytes = extractBits(pixels, 0, 32);
  const length = new DataView(lengthBytes.buffer).getUint32(0, false);

  // Validate
  const maxCapacity = (availableBits - 32) >> 3;
  if (length === 0 || length > maxCapacity) return null;

  // Extract payload
  return extractBits(pixels, 32, length * 8);
}

/**
 * Map an RGB channel index (0,1,2,3,4,...) to the corresponding
 * RGBA pixel array index, skipping every alpha channel (index % 4 === 3).
 */
function rgbToRgbaIndex(rgbIdx: number): number {
  const pixel = Math.floor(rgbIdx / 3);
  const channel = rgbIdx % 3;
  return pixel * 4 + channel;
}

/**
 * Extract `numBits` LSBs from pixel RGBA data starting at RGB bit offset.
 */
function extractBits(
  pixels: Uint8ClampedArray,
  offsetBits: number,
  numBits: number
): Uint8Array {
  const result = new Uint8Array(numBits >> 3);
  for (let i = 0; i < numBits; i++) {
    const pixelIdx = rgbToRgbaIndex(offsetBits + i);
    const bit = pixels[pixelIdx]! & 1;
    const byteIdx = i >> 3;
    const bitPos = 7 - (i & 7); // MSB-first
    result[byteIdx]! |= bit << bitPos;
  }
  return result;
}
